import { AddCircle, RemoveCircleOutline } from "@mui/icons-material";
import { Chip } from "@mui/material";
import React from "react";

interface EventRegistrationButtonProps {
  isUserSignedUp: boolean;
  isRegistrationOpen: boolean;
  isRegistrationFull: boolean;
  onSignUp: () => Promise<void>;
  onSignOut: () => Promise<void>;
  size: "small" | "medium";
}
/**
 * Component that displays the registration button for an event
 * @param isUserSignedUp Whether the user is already signed up for the event, used to determine which button to display
 * @param isRegistrationOpen Whether the registration for the event is open, used to determine if the sign up and sign out buttons should be disabled
 * @param isRegistrationFull Whether the registration for the event is full, used to determine if the sign up button should be disabled
 * @param onSignUp The function to call when the user clicks the sign up button
 * @param onSignOut The function to call when the user clicks the sign out button
 * @param size The size of the button, passed down from the parent component to ensure consistent sizing with other buttons in the event details page
 * @returns A Chip component that displays the correct button based on the event and the user's sign up status
 */
const EventRegistrationButton = ({
  isUserSignedUp,
  isRegistrationOpen,
  isRegistrationFull,
  onSignUp,
  onSignOut,
  size,
}: EventRegistrationButtonProps) => {
  // If the user is signed up, return sign out button
  if (isUserSignedUp) {
    // if the registration is open, allow user to sign out
    if (isRegistrationOpen) {
      return (
        <Chip
          label="Abmelden"
          color="error"
          size={size}
          variant="outlined"
          icon={<RemoveCircleOutline />}
          onClick={onSignOut}
        />
      );

      // if the registration is not open, disable the sign out button
    } else {
      return (
        <Chip label="Abmelden" color="default" size={size} variant="outlined" icon={<RemoveCircleOutline />} disabled />
      );
    }
  }

  // If the user is not signed up, return sign up button
  if (!isUserSignedUp) {
    // If the registration is full, disable the sign up button
    if (isRegistrationFull) {
      return <Chip label="Keine Plätze" color="default" size={size} disabled />;
    }

    // If the registration is not open, disable the sign up button
    if (!isRegistrationOpen) {
      return <Chip label="Anmelden" color="default" size={size} disabled icon={<AddCircle />} />;
    }

    // if the registration is open and not full, allow user to sign up
    if (isRegistrationOpen && !isRegistrationFull) {
      return <Chip label="Anmelden" color="success" size={size} icon={<AddCircle />} onClick={onSignUp} />;
    }
  }

  // Default case, should not be reached
  return <Chip label="Anmelden" color="default" size={size} disabled icon={<AddCircle />} />;
};

export default EventRegistrationButton;
